import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * AnimatedSelect - A smooth, responsive dropdown component with CSS animations
 * Props:
 * - value: current value
 * - options: array of {label, value} objects
 * - placeholder: string
 * - onChange: (value) => void
 * - multiple: allow multi-select; value should be an array
 * - className: extra class
 * - maxDisplayItems: maximum number of items to display in multiselect (default: 2)
 */
const AnimatedSelect = ({ 
  value, 
  options = [], 
  placeholder = "Select", 
  onChange, 
  className = "",
  multiple = false,
  maxDisplayItems = 5,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = multiple
    ? options.filter(opt => Array.isArray(value) && value.map(String).includes(String(opt.value)))
    : options.find(opt => String(opt.value) === String(value));

  const updatePosition = useCallback(() => {
    if (toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Find the filteres_div container
      const filterContainer = document.querySelector('.filteres_div');
      let containerRect = null;
      
      if (filterContainer) {
        containerRect = filterContainer.getBoundingClientRect();
      }
      
      // Fixed height for dropdown with scroll
      const menuHeight = 200; // Match CSS max-height
      
      // Calculate space within the container
      let spaceBelow, spaceAbove;
      let top, left;
      
      if (containerRect) {
        // Position relative to the container
        spaceBelow = containerRect.bottom - rect.bottom;
        spaceAbove = rect.top - containerRect.top;
        
        // More aggressive below positioning - always try below first
        if (spaceBelow >= menuHeight + 20) {
          // Position below the field with small gap
          top = rect.bottom + 4;
        } else if (spaceBelow >= 50) {
          // Even if space is limited, try below if there's at least 50px
          top = rect.bottom + 4;
        } else {
          // Only position above if there's very little space below
          top = rect.top - menuHeight;
        }
        
        // Align dropdown to field start, but within container bounds
        left = Math.max(containerRect.left + 8, rect.left);
        left = Math.min(left, containerRect.right - 200); // Ensure dropdown fits in container
      } else {
        // Fallback to viewport positioning
        spaceBelow = viewportHeight - rect.bottom;
        spaceAbove = rect.top;
        
        if (spaceBelow >= menuHeight + 10) {
          top = rect.bottom + 4;
        } else {
          top = rect.top - menuHeight;
        }
        
        left = Math.max(8, rect.left);
      }
      
      // Ensure position is valid and within bounds
      let finalTop = top;
      
      if (containerRect) {
        // Ensure dropdown stays within container
        if (finalTop < containerRect.top + 8) {
          finalTop = containerRect.top + 8;
        } else if (finalTop + menuHeight > containerRect.bottom - 8) {
          finalTop = containerRect.bottom - menuHeight - 8;
        }
      } else {
        // Fallback to viewport bounds
        if (finalTop < 8) {
          finalTop = 8;
        } else if (finalTop + menuHeight > viewportHeight - 8) {
          finalTop = viewportHeight - menuHeight - 8;
        }
      }
      
      // Debug logging to understand positioning
      console.log('AnimatedSelect Container Debug:', {
        containerFound: !!containerRect,
        containerRect: containerRect ? {
          top: containerRect.top,
          bottom: containerRect.bottom,
          left: containerRect.left,
          right: containerRect.right
        } : null,
        fieldRect: { top: rect.top, bottom: rect.bottom, left: rect.left },
        space: { below: spaceBelow, above: spaceAbove },
        menuHeight: menuHeight,
        calculated: { top, left },
        final: { top: finalTop, left: left }
      });
      
      setPosition({
        top: finalTop,
        left: left,
        width: 'auto'
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      
      const handleResize = () => {
        // Recalculate position on window resize (including dev tools open/close)
        requestAnimationFrame(updatePosition);
      };
      const handleScroll = () => {
        // Update position on scroll to keep dropdown attached to field
        updatePosition();
      };
      const handleClickOutside = (e) => {
        if (toggleRef.current && !toggleRef.current.contains(e.target) && 
            menuRef.current && !menuRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('mousedown', handleClickOutside);
      // Also listen for orientation changes (mobile)
      window.addEventListener('orientationchange', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('orientationchange', handleResize);
      };
    }
  }, [isOpen, options.length, updatePosition]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue) => {
    if (multiple) {
      const current = Array.isArray(value) ? value.map(String) : [];
      const ov = String(optionValue);
      let next;
      if (current.includes(ov)) {
        next = current.filter(v => v !== ov);
      } else {
        next = [...current, ov];
      }
      onChange && onChange(next);
    } else {
      onChange && onChange(optionValue);
      setIsOpen(false);
    }
  };

  return (
    <div className={`animated-select ${className}`}>
      <button
        ref={toggleRef}
        className="animated-select__toggle"
        onClick={handleToggle}
        type="button"
      >
        <span className="animated-select__label">
          {multiple
            ? (Array.isArray(selectedOption) && selectedOption.length > 0
                ? (() => {
                    // Filter out "All" option from display - show only actual selected items
                    const displayOptions = selectedOption.filter(o => String(o.value) !== "All");
                    
                    // If no display options (only "All" was selected), show "All Partners"
                    if (displayOptions.length === 0) {
                      const allOption = selectedOption.find(o => String(o.value) === "All");
                      return allOption ? allOption.label : placeholder;
                    }
                    
                    // Show limited items with "+X more" if there are more
                    if (displayOptions.length > maxDisplayItems) {
                      const displayed = displayOptions.slice(0, maxDisplayItems).map(o => o.label).join(', ');
                      const remaining = displayOptions.length - maxDisplayItems;
                      return `${displayed} +${remaining} more`;
                    }
                    return displayOptions.map(o => o.label).join(', ');
                  })()
                : placeholder)
            : (selectedOption ? selectedOption.label : placeholder)
          }
        </span>
        <span className={`animated-select__caret ${isOpen ? 'open' : ''}`}>
          ▼
        </span>
      </button>
      
      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="animated-select__menu"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 99999999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {options.length === 0 ? (
            <div className="animated-select__empty">No options</div>
          ) : (
            options.map((option) => (
              <div
                key={String(option.value)}
                className={`animated-select__item ${
                  multiple
                    ? (Array.isArray(value) && value.map(String).includes(String(option.value)) ? 'active' : '')
                    : (String(option.value) === String(value) ? 'active' : '')
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {multiple && (
                  <input
                    type="checkbox"
                    readOnly
                    checked={Array.isArray(value) && value.map(String).includes(String(option.value))}
                    style={{ marginRight: 8 }}
                  />
                )}
                {option.label}
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default AnimatedSelect;
